import { Device, Project, RuuviTag, User, UserAlertRule } from '../types'
import { useTranslation } from 'react-i18next'
import {
  updateUserAlertRulesByDevice,
  updateUserAlertRulesByProject,
  updateUserAlertRulesByRuuviTag
} from '../services/userService'
import { AlertGroupName, AlertRuleName } from '../enums'
import { ChangeEvent, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { usePolicy } from '../hooks'
import { setUserAlertRules } from '../redux/slices/users'
import { RootState } from '../redux'
import { IconButton, Switch, SwitchProps, Tooltip } from '@mui/material'
import { Warning } from '@mui/icons-material'
import UserAlertRuleInput from '../types/UserAlertRuleInput'
import useUserAlertRules from '../hooks/useUserAlertRules'

export interface UserAlertRuleEnableSwitchProps
  extends Omit<SwitchProps, 'children' | 'loading' | 'onClick'> {
  type: 'email' | 'sms' | 'app'
  user: User
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  ruleGroupName: AlertGroupName
  alertRuleName: AlertRuleName
  userAlertRules?: UserAlertRule[]
  useRedux?: boolean
  beforeEnable?: (input: UserAlertRuleInput) => void
  afterEnable?: (userAlertRules: UserAlertRule[]) => void
}

export default function UserAlertRuleEnableSwitch({
  type,
  user,
  project,
  device,
  ruuviTag,
  ruleGroupName,
  alertRuleName,
  userAlertRules = [],
  useRedux = true,
  beforeEnable,
  afterEnable,
  size,
  disabled,
  ...props
}: UserAlertRuleEnableSwitchProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The policy state.
   */
  const { canEditUserAlertRules } = usePolicy()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The user alert rules from redux.
   */
  const userAlertRulesFromRedux = useSelector((state: RootState) => state.users.userAlertRules)

  /**
   * The used user alert rules.
   */
  const usedUserAlertRules = useRedux ? userAlertRulesFromRedux : userAlertRules

  const { updateUserAlertRules } = useUserAlertRules({
    user,
    userAlertRules: usedUserAlertRules,
    project,
    device,
    ruuviTag,
    useRedux
  })

  /**
   * The checked state.
   */
  const [checked, setChecked] = useState<boolean>(false)

  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(false)

  /**
   * Indicates if the device or ruuvitag is without a project.
   */
  const hasNoProject = !project?.id && !device?.asset?.projectId && !ruuviTag?.asset?.projectId

  /**
   * Indicates if the user has enabled project app notifications.
   */
  const hasEnabledProjectAppNotifications = Boolean(
    user?.projects?.find(
      (membership) =>
        membership.projectId === project?.id ||
        membership.projectId === device?.asset?.projectId ||
        membership.projectId === ruuviTag?.asset?.projectId
    )?.showInAppNotifications
  )

  const canEnableUserAlertRule = (type: 'email' | 'sms' | 'app') => {
    switch (type) {
      case 'email':
        return user.notificationsByEmail
      case 'sms':
        return user.notificationsBySms
      case 'app':
        return user.notificationsInApp && (hasNoProject || hasEnabledProjectAppNotifications)
    }
  }

  /**
   * Dispatch a success notification.
   */
  function dispatchSuccessNotification() {
    switch (type) {
      case 'email':
        dispatch(createSuccessNotification(t('enableEmailNotificationSuccess')))
        break
      case 'sms':
        dispatch(createSuccessNotification(t('enableSmsNotificationSuccess')))
        break
      case 'app':
        dispatch(createSuccessNotification(t('enableAppNotificationSuccess')))
        break
    }
  }

  /**
   * Dispatch an error notification.
   */
  function dispatchErrorNotification() {
    switch (type) {
      case 'email':
        dispatch(createErrorNotification(t('enableEmailNotificationError')))
        break
      case 'sms':
        dispatch(createErrorNotification(t('enableSmsNotificationError')))
        break
      case 'app':
        dispatch(createErrorNotification(t('enableAppNotificationError')))
        break
    }
  }

  /**
   * Handle the "enable" action.
   */
  async function handleChange(_event: ChangeEvent<HTMLInputElement>, checked: boolean) {
    if (checked) {
      try {
        setLoading(true)

        const input: UserAlertRuleInput = {
          ruleGroupName,
          alertRuleName,
          emailNotificationEnabled: type === 'email',
          smsNotificationEnabled: type === 'sms',
          appNotificationEnabled: type === 'app'
        }

        beforeEnable?.(input)

        const updatedUserAlertRules = await updateUserAlertRules(input)

        // Turn the switch "on" after success.
        setChecked(true)

        // Wait for the switch animation to end for smoother transition.
        setTimeout(() => {
          dispatchSuccessNotification()
          afterEnable?.(updatedUserAlertRules)
        }, 50)
      } catch (_error) {
        // Keep the switched "turned off" when an error occurs.
        setChecked(false)
        dispatchErrorNotification()
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <>
      {canEditUserAlertRules(user) &&
        (canEnableUserAlertRule(type) ? (
          <Switch
            checked={checked}
            size={size}
            disabled={loading || disabled}
            onChange={handleChange}
            {...props}
          />
        ) : (
          <>
            {type === 'email' ? (
              <Tooltip title={t('emailNotificationsDisabled')}>
                <IconButton size={size} color="warning">
                  <Warning />
                </IconButton>
              </Tooltip>
            ) : type === 'sms' ? (
              <Tooltip title={t('smsNotificationsDisabled')}>
                <IconButton size={size} color="warning">
                  <Warning />
                </IconButton>
              </Tooltip>
            ) : type === 'app' ? (
              <Tooltip title={t('appNotificationsDisabled')}>
                <IconButton size={size} color="warning">
                  <Warning />
                </IconButton>
              </Tooltip>
            ) : null}
          </>
        ))}
    </>
  )
}
